import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectAuth } from '../../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../../services/user/selectors'
import { EN } from '../../../services/languages/constants'
import type { VideoPromptSuggestionsInput } from '../../../graphql-types'
import { useVideoPromptSuggestionsLazyQuery } from '../queries.generated'
import { useVideoState } from '../../OracleCommons/useVideoState'

/**
 * Suggestion item returned from the API
 */
export interface VideoPromptSuggestion {
  prompt: string
  focus: string
  rationale: string
}

/**
 * Return type for the useVideoPromptSuggestions hook
 */
export interface UseVideoPromptSuggestionsResult {
  suggestions: VideoPromptSuggestion[] | null
  coverageStartTimecode: string | null
  coverageEndTimecode: string | null
  refetch: (excludePrompts?: string[]) => void
}

/**
 * Hook options
 */
export interface UseVideoPromptSuggestionsOptions {
  excludePrompts?: string[]
}

/**
 * Hook to fetch video prompt suggestions based on current playback position.
 *
 * Encapsulates:
 * - Video state from Redux (contentId, featureMediaId)
 * - Video.js player access for current time
 * - Polling until player is ready
 * - Single fetch of suggestions (fetches only once per mount)
 * - Optional exclusion of previously clicked prompts
 *
 * The hook polls every 200ms until the video player reports a valid currentTime,
 * then fetches suggestions and stops polling.
 */
export function useVideoPromptSuggestions (
  options: UseVideoPromptSuggestionsOptions = {},
): UseVideoPromptSuggestionsResult {
  const { excludePrompts } = options
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt')
  const language = useSelector(selectPrimaryLanguage) || EN
  const { contentId, featureMediaId, getPlaybackState } = useVideoState()
  const [fetchSuggestions, { data }] = useVideoPromptSuggestionsLazyQuery()
  const hasFetched = useRef(false)

  useEffect(() => {
    if (!contentId || !featureMediaId || !jwt) return
    if (hasFetched.current) return

    const tryFetch = () => {
      const { currentTimeSeconds } = getPlaybackState()
      if (currentTimeSeconds == null) return false

      hasFetched.current = true
      const input: VideoPromptSuggestionsInput = {
        contentId,
        featureMediaId,
        currentTimeSeconds,
        language,
      }

      // Only add excludePrompts if we have actual prompts to exclude
      // This keeps the GraphQL request clean (field omitted when not needed)
      if (excludePrompts && excludePrompts.length > 0) {
        input.excludePrompts = excludePrompts
      }

      fetchSuggestions({
        variables: { input },
        context: { jwt },
      })
      return true
    }

    // Try immediately, if player not ready poll until it is
    if (!tryFetch()) {
      const interval = setInterval(() => {
        if (tryFetch()) {
          clearInterval(interval)
        }
      }, 200)
      // eslint-disable-next-line consistent-return
      return () => { clearInterval(interval) }
    }
  }, [contentId, featureMediaId, jwt, language, getPlaybackState, fetchSuggestions, excludePrompts])

  const suggestions = data?.videoPromptSuggestions?.suggestions ?? null

  // Refetch suggestions with current timestamp (e.g., after user clicks a prompt)
  // Can optionally exclude specific prompts to prevent duplicates
  const refetch = useCallback((excludePromptsOverride?: string[]) => {
    hasFetched.current = false
    const { currentTimeSeconds } = getPlaybackState()
    if (currentTimeSeconds != null && contentId && featureMediaId && jwt) {
      hasFetched.current = true

      const input: VideoPromptSuggestionsInput = {
        contentId,
        featureMediaId,
        currentTimeSeconds,
        language,
      }

      // Use override if provided, otherwise use options.excludePrompts
      // Only add excludePrompts field if we have actual prompts to exclude
      const excludeList = excludePromptsOverride !== undefined
        ? excludePromptsOverride
        : excludePrompts
      if (excludeList && excludeList.length > 0) {
        input.excludePrompts = excludeList
      }

      fetchSuggestions({
        variables: { input },
        context: { jwt },
      })
    }
  }, [contentId, featureMediaId, jwt, language, getPlaybackState, fetchSuggestions, excludePrompts])

  const coverageStartTimecode = data?.videoPromptSuggestions?.coverageStartTimecode ?? null
  const coverageEndTimecode = data?.videoPromptSuggestions?.coverageEndTimecode ?? null

  return {
    suggestions,
    coverageStartTimecode,
    coverageEndTimecode,
    refetch,
  }
}
